// src/utils/validateMp4.js
// Checks that an uploaded MP4 will play on (nearly) every browser and device:
//   - it is a real MP4 container,
//   - the video track is H.264 (HEVC / AV1 / VP9 only play on some devices),
//   - the audio track, if any, is AAC (AC-3 / E-AC-3 / Opus / ALAC only play on some devices),
//   - this browser can actually load it.
// Resolves to { warning } (warning may be '') or rejects with an Error whose message is user-facing.

const BAD_AUDIO = ['ac-3', 'ec-3', 'Opus', 'alac'];

// Finds the 'moov' box (where codecs are declared) among the file's top-level boxes.
const readTopLevelBoxes = (buffer) => {
  const view = new DataView(buffer);
  const boxes = [];
  let pos = 0;
  while (pos + 8 <= buffer.byteLength) {
    let size = view.getUint32(pos);
    const type = String.fromCharCode(
      view.getUint8(pos + 4), view.getUint8(pos + 5), view.getUint8(pos + 6), view.getUint8(pos + 7)
    );
    if (size === 1 && pos + 16 <= buffer.byteLength) {
      size = Number(view.getBigUint64(pos + 8)); // 64-bit size
    } else if (size === 0) {
      size = buffer.byteLength - pos; // box runs to the end of the file
    }
    if (size < 8) break;
    boxes.push({ type, start: pos, size });
    pos += size;
  }
  return boxes;
};

const canBrowserLoad = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  let settled = false;
  const finish = (error) => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    URL.revokeObjectURL(url);
    video.removeAttribute('src');
    video.load();
    if (error) reject(error); else resolve();
  };
  const timer = setTimeout(() => finish(new Error('The video took too long to load. Please try a smaller or re-exported MP4.')), 10000);
  video.preload = 'metadata';
  video.muted = true;
  video.onloadedmetadata = () => {
    if (video.videoWidth > 0 && video.videoHeight > 0) finish();
    else finish(new Error('The file has no video picture. Please upload an MP4 (H.264) video.'));
  };
  video.onerror = () => finish(new Error('This video could not be played. Please export it as MP4 (H.264 video, AAC audio).'));
  video.src = url;
});

export const validateMp4 = async (file) => {
  const buffer = await file.arrayBuffer();
  const boxes = readTopLevelBoxes(buffer);

  const ftyp = boxes.find(b => b.type === 'ftyp');
  const moov = boxes.find(b => b.type === 'moov');
  if (!ftyp || !moov) {
    throw new Error('This is not a valid MP4 file. Please export it as MP4 (H.264 video, AAC audio).');
  }

  // Codec names are written as 4-character tags inside the 'moov' box
  const moovText = new TextDecoder('latin1').decode(new Uint8Array(buffer, moov.start, moov.size));
  if (!moovText.includes('avc1') && !moovText.includes('avc3')) {
    throw new Error('The video is not H.264, which does not play on every device. Please re-export it as MP4 with H.264 video.');
  }
  const badAudio = BAD_AUDIO.find(tag => moovText.includes(tag));
  if (badAudio) {
    throw new Error('The audio is not AAC, which does not play on every device. Please re-export it as MP4 with AAC audio.');
  }

  await canBrowserLoad(file);

  // If the index is stored after the video data the browser has to seek to the end before it can
  // start, so playback starts slowly.
  const mdat = boxes.find(b => b.type === 'mdat');
  const warning = mdat && moov.start > mdat.start
    ? 'Tip: this video will start slowly because it is not optimised for web streaming ("fast start" / "web optimised" when exporting).'
    : '';
  return { warning };
};
